import { Code, ConnectError, createClient, type Client } from '@connectrpc/connect';
import type { ExportFlowStep, RunExportFlowResult } from './pcfExportFlow';
import { PcfExportService } from './gen/pcf_export_service_pb';
import { mapToGrpcPcfSettings, type GrpcSettingsPayload } from './pcfExportSettingsMapper';
import { getGrpcTransport } from './transport';
import {
  getActivePipeexProfile,
  getPipeexSettings,
  type ActivePipeexProfile,
  type PcfSettingsDto,
} from '../pipeex/pcfSettingsClient';
import { summarizePcfSettingsDtoForDebug } from '../pipeex/pcfSettingsDebug';
import {
  getPcfGroupingConfigurationError,
  overlayPcfGroupingConfiguration,
  resolvePcfGroupingConfiguration,
} from '../pipeex/pcfGroupingConfiguration';
import { normalizeSystemGroupingPreference } from '../pipeex/pcfSettingsSerialization';
import { generateTraceId, makeTraceparent } from '../traceparent';
import { useAuthStore } from '../../stores/useAuthStore';
import { useConfigurationStore } from '../../stores/useConfigurationStore';
import { webLog } from './webLogger';

type PcfExportGrpcClient = Client<typeof PcfExportService>;

type ExportFailureExtras = Pick<
  RunExportFlowResult,
  | 'errorCode'
  | 'versionMismatch'
  | 'quotaExhausted'
  | 'quotaInsufficient'
  | 'isDemo'
  | 'remainingExportElements'
  | 'groupFailures'
  | 'filesWritten'
  | 'skippedFiles'
  | 'fileIssues'
  | 'successfulFiles'
  | 'elementsWritten'
  | 'outputPath'
>;

interface PreparedPcfExportSettings {
  readonly local: PcfSettingsDto;
  readonly grpc: GrpcSettingsPayload;
}

export interface ExportFlowContext {
  readonly client: PcfExportGrpcClient;
  readonly settings: PreparedPcfExportSettings;
  readonly runId: string;
  readonly traceId: string;
  readonly traceparent: string;
  /** Name of the profile the settings were resolved from — surfaced in
   *  user-facing diagnostics so there is never doubt which profile drove
   *  the export. */
  readonly profileName: string;
}

export type StepResult<T> = { readonly ok: true; readonly value: T } | {
  readonly ok: false;
  readonly failure: RunExportFlowResult;
};

interface ExportFailureDetails {
  readonly traceId: string;
  readonly step: ExportFlowStep;
  readonly error: string;
  readonly extras?: ExportFailureExtras;
}

export function resolveProfileGrouping(advancedSettings: string): string {
  return resolvePcfGroupingConfiguration(advancedSettings).groupingMode;
}

export function createExportFailure({
  traceId,
  step,
  error,
  extras = {},
}: ExportFailureDetails): RunExportFlowResult {
  return { success: false, traceId, step, error, ...extras };
}

export function isUnimplementedError(error: unknown): boolean {
  if (error instanceof ConnectError) {
    return error.code === Code.Unimplemented;
  }
  if (error && typeof error === 'object' && 'code' in error && error.code === 12) {
    return true;
  }
  const message = error instanceof Error ? error.message : String(error);
  return /unimplemented/i.test(message);
}

export async function prepareExportFlow(profile?: ActivePipeexProfile): Promise<StepResult<ExportFlowContext>> {
  const runIdHex = generateTraceId();
  const runId = `${runIdHex.slice(0, 8)}-${runIdHex.slice(8, 12)}-${runIdHex.slice(12, 16)}-${runIdHex.slice(16, 20)}-${runIdHex.slice(20)}`;
  const traceId = generateTraceId();
  const traceparent = makeTraceparent(traceId);
  if (!useAuthStore.getState().accessToken) {
    webLog.warn('export-orchestrator', `[no-token] traceId=${traceId} aborting export`);
    return {
      ok: false,
      failure: createExportFailure({
        traceId,
        step: 'license',
        error: 'Please sign in to export.',
      }),
    };
  }

  const transport = getGrpcTransport();
  if (!transport) {
    return {
      ok: false,
      failure: createExportFailure({
        traceId,
        step: 'calculate',
        error: 'PipeEX core runtime is unreachable. Reload the Revit add-in and retry.',
      }),
    };
  }

  try {
    const activeProfile = profile ?? getActivePipeexProfile();
    const storedSettings = await getPipeexSettings(activeProfile.name, activeProfile.settingsType);
    // The calculate request's grouping enum already follows the live export
    // dialog; the settings payload must carry the same grouping, otherwise an
    // unsaved parameter choice arrives plugin-side with no parameter name.
    const menuConfiguration = useConfigurationStore.getState().menuConfiguration;
    const effectiveAdvancedSettings = overlayPcfGroupingConfiguration(
      storedSettings.advancedSettings ?? '',
      {
        groupingMode: normalizeSystemGroupingPreference(menuConfiguration.systemGroupingValue),
        parameterName: menuConfiguration.groupingParameterName ?? '',
        source: menuConfiguration.groupingParameterSource === 'Type' ? 'Type' : 'Element',
      }
    );
    const localSettings: PcfSettingsDto = {
      ...storedSettings,
      advancedSettings: effectiveAdvancedSettings,
    };
    const groupingConfiguration = resolvePcfGroupingConfiguration(effectiveAdvancedSettings);
    const groupingError = getPcfGroupingConfigurationError(
      groupingConfiguration.groupingMode,
      groupingConfiguration.parameterName,
    );
    if (groupingError) {
      webLog.warn(
        'export-orchestrator',
        `[profile-settings-invalid] traceId=${traceId} effectiveGrouping=${groupingConfiguration.groupingMode} missingGroupingParameter=true`,
      );
      return {
        ok: false,
        failure: createExportFailure({
          traceId,
          step: 'calculate',
          error: groupingError,
        }),
      };
    }
    const summary = summarizePcfSettingsDtoForDebug(localSettings);
    webLog.info(
      'export-orchestrator',
      `[profile-settings-resolved] traceId=${traceId} `
        + `settingsType=${localSettings.settingsType} effectiveGrouping=${groupingConfiguration.groupingMode} `
        + `projectMappings=${summary.counts.projectParameters} `
        + `systemMappings=${summary.counts.systemParameters} isoMappings=${summary.counts.isoAttributes} `
        + `validationMappings=${summary.counts.validationEntries}`,
    );
    return {
      ok: true,
      value: {
        client: createClient(PcfExportService, transport),
        settings: { local: localSettings, grpc: mapToGrpcPcfSettings(localSettings) },
        runId,
        traceId,
        traceparent,
        profileName: activeProfile.name,
      },
    };
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    return {
      ok: false,
      failure: createExportFailure({
        traceId,
        step: 'calculate',
        error: `PCF export settings could not be prepared from the active profile: ${detail}`,
      }),
    };
  }
}
